import { compact, get, isObject, map } from 'lodash';
import moment from 'moment';
import { select, takeEvery } from 'redux-saga/effects';
import ConnectionHandler from 'relay-connection-handler-plus';
import { commitLocalUpdate } from 'relay-runtime';
import { createAsyncAction, getType } from 'typesafe-actions';
import { LoggerStatusEnum, sendLog } from 'ui-model';

import { selectCurrentUser } from '@lib/selectors';
import environment from '@lib/utils/RelayEnvironment';

import { invalidateRecord, toGlobalId } from 'services/relayService';

export const actionCreateMessage = createAsyncAction(
  'actionCreateMessage/REQUEST',
  'actionCreateMessage/SUCCESS',
  'actionCreateMessage/FAILURE',
)<any, any, any>();

const connectionKey = 'InfiniteScrollMessages_messagesConnection';

function* sagaWatcherMessageSend(action: ReturnType<typeof actionCreateMessage.request>) {
  const {
    payload: { message, fakeId, file },
  } = action;
  try {
    const patient = get(message, 'patient');
    const facility = get(message, 'facility');
    const chatActivity = get(message, 'chatActivity');
    const hasFacilityOnMessage = facility !== 'all' && !!facility;
    if (!patient) {
      throw new Error('data missing');
    }

    // @ts-ignore
    const currentUser = yield select(selectCurrentUser);

    commitLocalUpdate(environment, (store) => {
      try {
        // Invalidate records
        invalidateRecord(store.get(toGlobalId('Patient', patient)));
        invalidateRecord(store.get(toGlobalId('ChatActivity', chatActivity)));

        // Update current connection dynamically
        const viewerRecord = store.get(toGlobalId('User', get(currentUser, '_id')));
        const patientRecord = store.get(toGlobalId('Patient', patient));
        const facilityRecord = store.get(toGlobalId('Facility', facility));
        const facilityNotFound = hasFacilityOnMessage && !facilityRecord;

        if (!viewerRecord || !patientRecord || facilityNotFound) {
          return;
        }
        const connectionRecord = ConnectionHandler.getConnection(patientRecord, connectionKey);
        if (!connectionRecord) {
          return;
        }
        // Invalidate connection just in case, to refetch next time it's rendered
        connectionRecord.invalidateRecord();

        // Create a new local Message record
        const newMessageRecord = store.create(fakeId, 'Message');
        // set values
        newMessageRecord.setValue(fakeId, 'fakeId');
        newMessageRecord.setValue(moment().valueOf(), 'createdAt');
        newMessageRecord.setValue('pending', 'status');
        // copy values
        newMessageRecord.setValue(isObject(file) ? true : file, 'oid_file');
        newMessageRecord.setValue(message.autoCloseChat, 'autoCloseChat');
        newMessageRecord.setValue(message.automated, 'automated');
        newMessageRecord.setValue(message.channel, 'channel');
        newMessageRecord.setValue(message.recipientType, 'recipientType');
        newMessageRecord.setValue(message.ref, 'ref');
        newMessageRecord.setValue(message.refId, 'refId');
        newMessageRecord.setValue(message.secure, 'secure');
        newMessageRecord.setValue(message.source, 'source');
        newMessageRecord.setValue(message.text, 'text');
        newMessageRecord.setValue(message.type, 'type');
        newMessageRecord.setValue(message.visibility, 'visibility');
        // set linked records
        newMessageRecord.setLinkedRecord(viewerRecord, 'createdBy');
        newMessageRecord.setLinkedRecord(patientRecord, 'patient');
        if (facilityRecord) {
          newMessageRecord.setLinkedRecord(facilityRecord, 'facility');
        }
        newMessageRecord.setValue(null, 'externalStatus');
        newMessageRecord.setValue(null, 'media');
        newMessageRecord.setValue(null, 'mentions');
        newMessageRecord.setValue(null, 'notifyTo');

        // Insert as last message
        const newEdge = ConnectionHandler.createEdge(store, connectionRecord, newMessageRecord, 'MessageEdge');
        ConnectionHandler.insertEdgeBefore(connectionRecord, newEdge);
      } catch (error) {
        sendLog('sagaWatcherMessageSend', { errorStack: error, context: action }, LoggerStatusEnum.error);
      }
    });
  } catch (error) {
    sendLog('sagaWatcherMessageSend', { errorStack: error, context: action }, LoggerStatusEnum.error);
  }
}

// eslint-disable-next-line require-yield
function* sagaWatcherMessageSendSuccess(action: any) {
  try {
    const message = get(action, 'payload.data');
    const _id = get(message, '_id');
    const fakeId = get(action, 'payload.requestPayload.fakeId');
    if (!_id || !fakeId) {
      sendLog('sagaWatcherMessageSendSuccess IDs missing', { context: action }, LoggerStatusEnum.warn);
      return;
    }

    commitLocalUpdate(environment, (store) => {
      const messageRecord = store.get(fakeId);
      if (!messageRecord) {
        sendLog('sagaWatcherMessageSendSuccess messageRecord missing', { context: action }, LoggerStatusEnum.warn);
        return;
      }

      // Temp record exists > update fakeId with the globalID so we can identify it later
      messageRecord.setValue(toGlobalId('Message', _id), 'fakeId');

      // Update `notifyTo` field dynamically
      const notifyToRecords = compact(
        map(message.notifyTo, (notifyTo) => {
          // @ts-ignore
          const notifyToGlobalId = toGlobalId('MessageNotifyTo', notifyTo._id);
          const record = store.get(notifyToGlobalId) || store.create(notifyToGlobalId, 'MessageNotifyTo');
          record.setValue(notifyTo._id, '_id');
          record.setValue(notifyTo.to, 'to');
          record.setValue(notifyTo.channel, 'channel');
          return record;
        }),
      );
      messageRecord.setLinkedRecords(notifyToRecords, 'notifyTo');
    });
  } catch (error) {
    sendLog('sagaWatcherMessageSendSuccess', { errorStack: error, context: action }, LoggerStatusEnum.error);
  }
}

export default function* sagaWatchersMessage() {
  // Sending message > immediate update
  yield takeEvery(getType(actionCreateMessage.request), sagaWatcherMessageSend);
  // Message sent > update in case it contains new informations from BE (eg squigglies)
  yield takeEvery(getType(actionCreateMessage.success), sagaWatcherMessageSendSuccess);
}
